import { useEffect } from 'react'

import z from 'zod'

import { useFormWithSchema } from '@fortem-monorepo/react-spa'
import {
  ConfirmationDialog,
  ConfirmationDialogProps,
  Form,
  FormError,
  FormField,
  FormItem,
  FormLabel,
  Textarea,
} from '@fortem-monorepo/react-ui'

import { FormFieldRow } from '@/components/forms/BookTradeForm/FormFieldRow'
import { usePnlContext } from '@/pages/pnl-attributions/context'
import { useApproveRequestMutation } from '@/pages/pnl-attributions/mutations'

export interface ApproveRequestDialogProps extends Partial<ConfirmationDialogProps> {
  mutationParams: {
    component_request_id: number
  }
}

const approveRequestSchema = z.object({
  component_request_id: z.number().nonnegative(),
  comment: z.string().min(1, 'You must provide a comment when approving a request.'),
})

export const ApproveRequestDialog: React.FC<ApproveRequestDialogProps> = ({ mutationParams, ...props }) => {
  const { params } = usePnlContext()
  const approveRequestMutation = useApproveRequestMutation(params)

  const form = useFormWithSchema(
    { mode: 'all', defaultValues: { component_request_id: mutationParams.component_request_id } },
    approveRequestSchema,
  )

  useEffect(() => {
    form.trigger()
  }, [])

  const onSubmit = form.handleSubmit(async (data) => {
    await approveRequestMutation.mutateAsync(data)
    props.onClose?.()
  })

  return (
    <ConfirmationDialog
      {...props}
      title={`Approve request`}
      description={`This will approve the request created, are you sure you want to do this?`}
      action={onSubmit}
      pending={approveRequestMutation.isPending}
      valid={form.formState.isValid}
    >
      <Form {...form}>
        <FormFieldRow>
          <FormField
            control={form.control}
            name="comment"
            render={({ field }) => {
              return (
                <FormItem>
                  <div className="flex items-center gap-2">
                    <FormLabel>Comment</FormLabel>
                    <FormError />
                  </div>
                  <Textarea value={field.value ?? ''} onChange={field.onChange} />
                </FormItem>
              )
            }}
          />
        </FormFieldRow>
      </Form>
    </ConfirmationDialog>
  )
}
